<template>
  <div class="register-view">
    <div class="wizard-container">
      <!-- Progress Bar -->
      <div class="progress-bar-container">
        <div class="progress-bar">
          <div class="progress" :style="{ width: `${progressPercentage}%` }"></div>
        </div>
      </div>
      
      <!-- Step Title -->
      <h1 class="wizard-title">{{ currentStepTitle }}</h1>

      <!-- Step Content -->
      <div class="wizard-content">
        <!-- Step 1: Initial Registration -->
        <RegisterStep1 
          v-if="currentStep === 1" 
          :form-data="formData" 
          @update:form-data="updateFormData" 
          @next-step="nextStep"
        />
          
        <!-- Step 2: Identity Verification -->
        <RegisterStep2 
          v-if="currentStep === 2" 
          :form-data="formData" 
          @update:form-data="updateFormData" 
          @next-step="nextStep"
          @prev-step="prevStep"
        />
          
        <!-- Step 3: Password Creation -->
        <RegisterStep3 
          v-if="currentStep === 3" 
          :form-data="formData" 
          @update:form-data="updateFormData" 
          @next-step="nextStep"
          @prev-step="prevStep"
        />
          
        <!-- Step 4: Confidentiality Agreement -->
        <RegisterConfidentiality
          v-if="currentStep === 4"
          :form-data="formData"
          @update:form-data="updateFormData"
          @next-step="nextStep"
          @prev-step="prevStep"
        />

        <!-- Step 5: User Profile -->
        <RegisterStep4 
          v-if="currentStep === 5" 
          :form-data="formData" 
          @update:form-data="updateFormData" 
          @next-step="nextStep"
          @prev-step="prevStep"
        />
 
        <!-- Step 6: Specialization Part 1 (previously Step 7) -->
        <RegisterStep6 
          v-if="currentStep === 6" 
          :form-data="formData" 
          @update:form-data="updateFormData" 
          @next-step="nextStep"
          @prev-step="prevStep"
        />
 
        <!-- Step 7: Specialization Part 2 (previously Step 8) -->
        <RegisterStep7 
          v-if="currentStep === 7" 
          :form-data="formData" 
          @update:form-data="updateFormData" 
          @next-step="nextStep"
          @prev-step="prevStep"
        />
 
        <!-- Step 8: Specialization Part 3 (previously Step 9) -->
        <RegisterStep8 
          v-if="currentStep === 8" 
          :form-data="formData" 
          @update:form-data="updateFormData" 
          @finish="finishRegistration"
          @prev-step="prevStep"
        />
      </div>
    </div>
  </div>
</template>

<script>
import RegisterStep1 from '@/components/register/RegisterStep1.vue';
import RegisterStep2 from '@/components/register/RegisterStep2.vue';
import RegisterStep3 from '@/components/register/RegisterStep3.vue';
import RegisterConfidentiality from '@/components/register/RegisterConfidentiality.vue';
import RegisterStep4 from '@/components/register/RegisterStep4.vue';
import RegisterStep6 from '@/components/register/RegisterStep6.vue';
import RegisterStep7 from '@/components/register/RegisterStep7.vue';
import RegisterStep8 from '@/components/register/RegisterStep8.vue';

export default {
  name: 'RegisterView',
  components: {
    RegisterStep1,
    RegisterStep2,
    RegisterStep3,
    RegisterConfidentiality,
    RegisterStep4,
    RegisterStep6,
    RegisterStep7,
    RegisterStep8
  },
  data() {
    return {
      currentStep: 1,
      totalSteps: 8,
      stepTitles: {
        1: 'Register',
        2: 'ID Verification',
        3: 'Email Verification',
        4: 'Confidentiality Agreement',
        5: 'Expert Profile Setup',
        6: 'Expertise Areas',  // Updated
        7: 'Age Demographics & Geographic Expertise',  // Updated 
        8: 'Professional Background'  // Updated
      },
      formData: {
        // Step 1
        lastName: '',
        mobilePhone: '',
        email: '',
        birthdate: '',
        agreeToTerms: false,
        
        // Step 2
        verificationCode: '',
        
        // Step 3
        password: '',
        confirmPassword: '',
        
        // Step 4
        fullName: '',
        profilePicture: null,
        curriculumHighlights: [],
        gender: null,
        
        // Step 5
        brands: [
          {
            name: '',
            startDate: '',
            endDate: '',
            involvedWithBrand: null
          }
        ],
        
        // Step 6
        specialization: '',
        expertiseLevel: null,
        designExpertise: {
          package: null,
          product: null,
          graphic: null
        },
        industries: [],
        
        // Step 7
        ageExpertise: {
          children: null,
          teenagers: null,
          adults: null,
          seniors: null
        },
        geoExpertise: {
          northAmerica: null,
          europe: null,
          asiaPacific: null,
          latinAmerica: null,
          middleEastAfrica: null
        },
        
        // Step 8
        background: {
          agency: null,
          design: null,
          academia: null,
          marketing: null,
          marketResearch: null
        }
      }
    };
  },
  computed: {
    progressPercentage() {
      // Calculate progress based on current step number
      return (this.currentStep / this.totalSteps) * 100;
    },
    currentStepTitle() {
      return this.stepTitles[this.currentStep];
    }
  },
  methods: {
    updateFormData(newData) {
      this.formData = { ...this.formData, ...newData };
    },
    nextStep() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    async finishRegistration() {
      try {
        console.log('Registration complete:', this.formData);

        // Ensure the Vuex store has user info so the navigation guard recognises authentication
        await this.$store.dispatch('fetchUserProfile');

        this.$router.push({ name: 'home' });
      } catch (e) {
        console.warn('Could not fetch user profile after registration:', e);
        // Even if fetching fails, proceed to home; guard will attempt again
        this.$router.push({ name: 'home' });
      }
    }
  }
}
</script>

<style scoped>
.register-view {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000;
  padding: 20px;
}

.wizard-container {
  width: 100%;
  max-width: 550px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.progress-bar-container {
  width: 100%;
  margin-bottom: 40px;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background-color: #444;
  position: relative;
}

.progress {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: #e94057;
  transition: width 0.3s ease;
}

.wizard-title {
  color: white;
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 30px;
  text-align: center;
}

.wizard-content {
  width: 100%;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
}
</style> 